import type { Lesson, QuizStep } from '../schema.js';

const WORTH_CHOICES = ['1', '3', '5', '9'];

function worthQuiz(worth: string): QuizStep {
  const choices = [...WORTH_CHOICES.filter((c) => c !== worth), worth];
  return {
    kind: 'quiz',
    title: 'How many pawns?',
    body: 'Time for a quick check!',
    questions: [
      {
        prompt: 'In pawns, how much is this piece worth?',
        choices,
        correct: choices.length - 1,
      },
    ],
  };
}

function pieceLesson(opts: {
  id: string;
  title: string;
  body: string;
  narration: string;
  worth: string;
}): Lesson {
  return {
    id: opts.id,
    slug: `piece-power-${opts.id}`,
    title: opts.title,
    audienceLevel: 'beginner',
    concepts: ['piece-power'],
    steps: [
      { kind: 'explain', title: opts.title, body: opts.body, narration: opts.narration },
      worthQuiz(opts.worth),
    ],
  };
}

export const PIECE_POWER_LESSONS: Lesson[] = [
  pieceLesson({
    id: 'pawn',
    title: 'Pawn — the little soldier',
    body: 'The pawn is the smallest piece, worth just 1 pawn. Pawns are slow but brave — a row of pawns protects everything behind them!',
    narration: 'The pawn is worth one pawn.',
    worth: '1',
  }),
  pieceLesson({
    id: 'knight',
    title: 'Knight — 3 pawns',
    body: 'The knight hops in an L shape and can jump over other pieces! It is worth 3 pawns.',
    narration: 'The knight is worth three pawns.',
    worth: '3',
  }),
  pieceLesson({
    id: 'bishop',
    title: 'Bishop — 3 pawns',
    body: 'The bishop slides diagonally and loves long open diagonals. It is worth 3 pawns, just like a knight.',
    narration: 'The bishop is worth three pawns.',
    worth: '3',
  }),
  pieceLesson({
    id: 'rook',
    title: 'Rook — 5 pawns',
    body: 'The rook slides straight along files and ranks. It is strong and worth 5 pawns.',
    narration: 'The rook is worth five pawns.',
    worth: '5',
  }),
  pieceLesson({
    id: 'queen',
    title: 'Queen — 9 pawns',
    body: 'The queen is the most powerful piece — she moves like a rook and a bishop together! She is worth 9 pawns.',
    narration: 'The queen is worth nine pawns.',
    worth: '9',
  }),
  {
    id: 'king',
    slug: 'piece-power-king',
    title: 'King — priceless!',
    audienceLevel: 'beginner',
    concepts: ['piece-power'],
    steps: [
      {
        kind: 'explain',
        title: 'The king is priceless',
        body: 'The king has no pawn value because you can never trade him away. If you lose your king, you lose the game. Protect your king!',
        narration: 'The king is priceless — you can never trade him away.',
      },
      {
        kind: 'quiz',
        title: 'Can you trade your king?',
        body: 'Time for a quick check!',
        questions: [
          {
            prompt: 'Can you ever trade your king, even for a hundred queens?',
            choices: ['Yes, why not?', 'Never — the king is priceless!'],
            correct: 1,
          },
        ],
      },
    ],
  },
  {
    id: 'trade-drill',
    slug: 'piece-power-trade',
    title: 'Trade like a pro',
    audienceLevel: 'beginner',
    concepts: ['piece-power'],
    steps: [
      {
        kind: 'explain',
        title: 'What is a trade?',
        body: 'When both sides capture pieces, it is called a trade. A good trade wins you more than you lose.',
      },
      {
        kind: 'drill',
        task: 'trade',
        title: 'Win the rook',
        body: 'Your pawn can capture the rook. Make the winning trade!',
        fen: '4k3/8/8/5r2/4P3/8/8/4K3 w - - 0 1',
        hint: 'Move your e4 pawn forward and to the right, onto the rook.',
        answer: ['exf5'],
      },
      {
        kind: 'quiz',
        title: 'Worth it?',
        body: 'A quick check on trading.',
        questions: [
          {
            prompt: 'You trade your pawn for the enemy rook. Is that a good trade?',
            choices: ['No, I lost material.', 'Yes, I win lots of material!'],
            correct: 1,
          },
        ],
      },
    ],
  },
];
